import React, { useCallback, useMemo, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Checkbox, Col, Form, Radio, Row, Select } from "antd";
import { useSelector } from "react-redux";
import { DictionariesSelector } from "store/dictionaries/selectors";
import AdditionalComments from "pages/meter-management/ready-for-repair/RepairMeter/Tabs/AdditionalComments";
import NGIFormItem from "components/Common/form/NGIFormItem";
import Title from "antd/es/typography/Title";
import NGIInput from "components/Common/inputs/NGIInput";
import NGISelect from "components/Common/inputs/NGISelect";
import { AccessoriesSelector } from "store/meter-management/configuration/accessories/selectors";
import classnames from "classnames";
import { getReasonsByType, getRequiredFieldsMapping } from "pages/meter-management/faulty-reasons/utils";
import NGICheckbox from "components/Common/inputs/NGICheckbox";

const useStyles = makeStyles({
  titleWithAction: {
    display: "flex",
    justifyContent: "space-between",
  },
  descriptionTitle: {
    display: "flex",
    flexDirection: "column",
    textAlign: "left",
    fontWeight: 600,

    "&>span": {
      fontSize: 13,
      fontWeight: 300,
    },
  },
  modalContent: {
    border: "1px solid rgba(0, 0, 0, 0.05)",
    borderLeft: "none",
    borderRight: "none",
    marginBottom: 24,
  },
  container: {
    display: "flex",
  },
  listBlock: {
    width: "100%",
  },
  item: {
    marginBottom: 20,
  },
  title: {
    textTransform: "uppercase",
    color: "#666666",
    fontWeight: 300,
    fontSize: 12,
  },
  value: {
    fontWeight: 600,
    fontSize: 14,
    color: "#000000",
    marginTop: 5,
  },
  description: {
    fontWeight: 600,
    fontSize: 14,
    margin: "15px 0",
  },
  filePreview: {
    width: 36,
    height: 36,
    borderRadius: 5,
    marginRight: 16,
  },
  simCardContainer: {
    margin: "10px 0",
  },
  checkboxContainer: {
    width: "100%",
  },
  checkboxRow: {
    marginBottom: 10,
  },
  subItem: {
    marginLeft: 20,
    marginTop: 10,
  },
  formTitle: {
    fontSize: "14px!important",
  },
  formRow: {
    width: "100%!important",
    marginBottom: 20,
  },
  requiredRepairFields: {
    display: "flex",
    alignItems: "flex-start",

    "&>div": {
      width: "40%",
    },
  },
});

const FaultyReasons = ({
  updateFaultyReason,
  className,
  meterType,
  reasons,
  showCommentSection = false,
  showRequiredRepairFields = false,
  multiple = false,
}) => {
  const classes = useStyles();
  const [selectedReasons, setSelectedReasons] = useState([]);
  const {
    list: { meterRepairInputFields },
  } = useSelector(DictionariesSelector);
  const { sensors } = useSelector(AccessoriesSelector);

  const onSelectCategory = useCallback(
    event => {
      const { value, checked } = event.target;

      if (multiple) {
        const toggleValue = (prev, value, checked) =>
          checked ? [...prev, value] : prev.filter(item => item !== value);
        updateFaultyReason(prev => toggleValue(prev, value, checked));
        setSelectedReasons(prev => toggleValue(prev, value, checked));
        return;
      }
      updateFaultyReason([value]);
      setSelectedReasons([value]);
    },
    [multiple]
  );

  const requiredFieldsMapping = useMemo(
    () => (reasons && meterRepairInputFields ? getRequiredFieldsMapping(reasons, meterRepairInputFields) : {}),
    [reasons, meterRepairInputFields]
  );

  const getIsFieldRequired = useCallback(
    field => selectedReasons.some(item => requiredFieldsMapping[field].includes(item)) || false,
    [requiredFieldsMapping, selectedReasons]
  );

  const reasonsList = useMemo(() => getReasonsByType(reasons, meterType), [meterType]);

  return (
    <>
      <div className={classnames(className, showRequiredRepairFields && classes.requiredRepairFields)}>
        <div>
          <Form.Item
            className={classes.checkboxContainer}
            name="faultyReasons"
            rules={[
              {
                required: true,
                message: multiple ? "Please select at least one faulty reasons" : "Please select the faulty reason",
              },
            ]}
          >
            {multiple ? (
              <Checkbox.Group className={classes.checkboxContainer}>
                {reasonsList?.map(item => (
                  <div key={item.item} className={classes.checkboxRow}>
                    <NGICheckbox onClick={onSelectCategory} value={item.item}>
                      {item.item}
                    </NGICheckbox>
                  </div>
                ))}
              </Checkbox.Group>
            ) : (
              <Radio.Group>
                {reasonsList?.map(item => (
                  <div key={item.item} className={classes.checkboxRow}>
                    <Radio onClick={onSelectCategory} value={item.item}>
                      {item.item}
                    </Radio>
                  </div>
                ))}
              </Radio.Group>
            )}
          </Form.Item>
        </div>
        {showRequiredRepairFields && (
          <Row className={classes.formRow}>
            {getIsFieldRequired("sensorSerial") && (
              <Col span={24}>
                <Title className={classes.formTitle}>Sensor Serial Number</Title>
                <div className={classes.formItem}>
                  <NGIFormItem
                    name="sensorSerial"
                    rules={[
                      { required: true, message: "Please enter value" },
                      {
                        pattern: /^[A-Z0-9]+$/,
                        message: "Please match the requested format. Only A-Z or 0-9 characters are allowed.",
                      },
                      {
                        max: 30,
                        message: "The max length is 30",
                      },
                    ]}
                  >
                    <NGIInput placeholder="Enter Sensor Serial Number" />
                  </NGIFormItem>
                </div>
              </Col>
            )}
            {getIsFieldRequired("sensorType") && (
              <Col span={24}>
                <Title className={classes.formTitle}>Sensor Type</Title>
                <div className={classes.formItem}>
                  <NGIFormItem name="sensorType" rules={[{ required: true, message: "Please enter value" }]}>
                    <NGISelect showSearch placeholder={"Select Sensor Type"} loading={!sensors.length}>
                      {sensors?.map(item => (
                        <Select.Option key={item.type} value={item.type}>
                          {item.type}
                        </Select.Option>
                      ))}
                    </NGISelect>
                  </NGIFormItem>
                </div>
              </Col>
            )}
            {getIsFieldRequired("pcb") && (
              <Col span={24}>
                <Title className={classes.formTitle}>PCB Serial Number</Title>
                <div className={classes.formItem}>
                  <NGIFormItem
                    name="pcb"
                    rules={[
                      { required: true, message: "Please enter value" },
                      {
                        max: 30,
                        message: "Please change the value. The max length is 30.",
                      },
                    ]}
                  >
                    <NGIInput placeholder="Enter PCB Serial Number" />
                  </NGIFormItem>
                </div>
              </Col>
            )}
            {getIsFieldRequired("removableIccid") && (
              <Col span={24}>
                <Title className={classes.formTitle}>Sim Card ICCID</Title>
                <div className={classes.formItem}>
                  <NGIFormItem
                    name="removableIccid"
                    rules={[
                      { required: true, message: "Please enter value" },
                      {
                        max: 20,
                        message: "Please change the value. The max length is 20.",
                      },
                    ]}
                  >
                    <NGIInput placeholder="Enter Sim Card ICCID" />
                  </NGIFormItem>
                </div>
              </Col>
            )}
          </Row>
        )}
      </div>

      {showCommentSection && <AdditionalComments />}
    </>
  );
};

export default FaultyReasons;
